import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Ensemble, Feel, MusicTags, StorySummary } from "../shared/story";
import { shuffleOpenings } from "../shared/story";
import { api } from "./api";
import { StoryStatus, saveSkin, savedSkin, scrollToLatest, useInkReveal, type HomeBits, type Skin, type ThemeProps } from "./common";
import { Admin } from "./admin";
import { Cloth } from "./cloth";
import { PatronPanel, type PatronReason } from "./patron";
import { BackChip, EndsNav, StoryMap, StoryRail } from "./rail";
import { sound } from "./sound";
import { noteOpened } from "./endings";
import { StoriesSheet } from "./stories";
import { BigMap, WhereYouAre } from "./noirmap";
import { Morning, morningDue } from "./morning";
import { takeNewSince } from "./new-since";
import type { Change } from "./changes";
import { Onboarding, ShortList, markOnboarded, onboarded } from "./onboarding";
import { Album } from "./plates";
import { PassOn, SeedWelcome } from "./seeds";
import { AccountSettings, DeleteSheet, Safety, Deleted, FeedbackSheet, Gate, Privacy, Sheet, ageOk, setAgeOk, type Meta } from "./trust";
import { toast, useStory } from "./useStory";
import { NitrateTheme } from "./themes/Nitrate";
import { CyanotypeTheme } from "./themes/Cyanotype";
import { EditionTheme } from "./themes/Edition";
import { ManuscriptTheme } from "./themes/Manuscript";
import { PhosphorTheme } from "./themes/Phosphor";
import { SkyLayer, Tear, useWorld } from "./v2/world";
import { Meanwhile, markMeanwhileSeen, meanwhileDue } from "./v2/meanwhile";

/**
 * Loom's client: one story engine (useStory), five looks the player can switch between at any time (skins.ts).
 * Typing while the narrator writes pauses the text; sending cuts the reply where you stopped reading.
 */

type Route = { kind: "home" } | { kind: "story"; id: string } | { kind: "plates" } | { kind: "privacy" } | { kind: "account" } | { kind: "safety" } | { kind: "admin" };
const routeOf = (path: string): Route => {
  if (path === "/plates") return { kind: "plates" };
  if (path === "/play") return { kind: "home" };
  if (path === "/privacy") return { kind: "privacy" };
  if (path === "/account") return { kind: "account" };
  if (path === "/safety") return { kind: "safety" };
  if (path === "/admin") return { kind: "admin" };
  const m = /^\/s\/([a-f0-9]{12})$/.exec(path);
  return m ? { kind: "story", id: m[1]! } : { kind: "home" };
};

/**
 * The story the player was last reading (this tab, else this browser), for the account page's back link: going back
 * a step in history landed on Stripe's "You're all done here" after a payment (owner, 2026-10-18).
 */
const LAST_STORY_KEY = "loom:last-story";
const rememberStory = (id: string) => { try { sessionStorage.setItem(LAST_STORY_KEY, id); localStorage.setItem(LAST_STORY_KEY, id); } catch { /* convenience only */ } };
const lastStory = (): string | null => {
  try { const id = sessionStorage.getItem(LAST_STORY_KEY) ?? localStorage.getItem(LAST_STORY_KEY); return id && /^[a-f0-9]{12}$/.test(id) ? id : null; } catch { return null; }
};

const THEMES: Record<Skin, (p: ThemeProps) => React.ReactNode> = {
  nitrate: NitrateTheme, manuscript: ManuscriptTheme, cyanotype: CyanotypeTheme, edition: EditionTheme, phosphor: PhosphorTheme,
};

export function App() {
  const [route, setRoute] = useState<Route>(() => routeOf(location.pathname));
  const [skin, setSkinState] = useState<Skin>(savedSkin);
  const setSkin = useCallback((s: Skin) => { saveSkin(s); setSkinState(s); }, []);
  useEffect(() => { document.documentElement.dataset.skin = skin; }, [skin]);
  const nav = useCallback((path: string, replace = false) => {
    if (replace) history.replaceState(null, "", path);
    else history.pushState(null, "", path);
    setRoute(routeOf(path));
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const pop = () => setRoute(routeOf(location.pathname));
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, []);
  const [age, setAge] = useState(ageOk);
  const [sheet, setSheet] = useState<"feedback" | "delete" | "patron" | "stories" | null>(null);
  const [patronWhy, setPatronWhy] = useState<PatronReason>(null);
  useEffect(() => {
    const on = (e: Event) => { setPatronWhy((e as CustomEvent<{ reason: PatronReason }>).detail?.reason ?? null); setSheet("patron"); };
    window.addEventListener("weftwake:patron", on);
    // Back from Stripe's checkout.
    const q = new URLSearchParams(location.search).get("patron");
    if (q === "thanks") { setPatronWhy("thanks"); setSheet("patron"); }
    // From the welcome page's "Send feedback".
    const fb = new URLSearchParams(location.search).get("feedback") === "1";
    if (fb) setSheet("feedback");
    if (q || fb) history.replaceState(null, "", location.pathname);
    return () => window.removeEventListener("weftwake:patron", on);
  }, []);
  const [deleted, setDeleted] = useState(false);
  // How to play: the tour (seven steps, over whatever is open) or the short list (a sheet).
  const [help, setHelp] = useState<"tour" | "list" | null>(null);
  // V2: the short list opened at one of its sections (the pen switch's "?", the intro's "How it works").
  const [helpAt, setHelpAt] = useState<string | undefined>(undefined);
  useEffect(() => {
    const on = (e: Event) => { setHelpAt((e as CustomEvent<string | undefined>).detail); setHelp("list"); };
    window.addEventListener("weftwake:help", on);
    return () => window.removeEventListener("weftwake:help", on);
  }, []);
  const [cloth, setCloth] = useState(false);
  useEffect(() => {
    const on = () => setCloth(true);
    window.addEventListener("weftwake:cloth", on);
    return () => window.removeEventListener("weftwake:cloth", on);
  }, []);
  const id = route.kind === "story" ? route.id : null;
  const page = route.kind === "story" ? "story" : route.kind === "home" ? (age ? "home" : "gate") : route.kind;
  // Requests to the app, from a link or typed into the Line. Nothing here reaches the narrator.
  const meta = useCallback((m: Meta) => {
    if (m === "privacy") nav("/privacy");
    else if (m === "account") nav("/account");
    else if (m === "safety") nav("/safety");
    else if (m === "export") location.assign("/api/export");
    else if (m === "help") { setHelpAt(undefined); setHelp("list"); }
    else if (m === "tour") setHelp("tour");
    else if (m === "cloth") setCloth(true);
    else if (m === "finale") window.dispatchEvent(new Event("weftwake:finale"));
    else if (m === "map") window.dispatchEvent(new Event("weftwake:map"));
    else if (m === "patron") { setPatronWhy(null); setSheet("patron"); }
    else setSheet(m);
  }, [nav]);

  let screen: React.ReactNode;
  if (deleted) screen = <Deleted onAgain={() => { setDeleted(false); nav("/", true); }} />;
  else if (route.kind === "admin") screen = <Admin goHome={() => nav("/")} />;
  else if (route.kind === "privacy") screen = <Privacy onBack={() => (history.length > 1 ? history.back() : nav("/"))} meta={meta} />;
  else if (route.kind === "safety") screen = <Safety onBack={() => (history.length > 1 ? history.back() : nav("/"))} meta={meta} />;
  else if (route.kind === "account") screen = <AccountSettings back={lastStory() ? "story" : "stories"} onBack={() => { const id = lastStory(); nav(id ? `/s/${id}` : "/play"); }} meta={meta} />;
  // Nothing is fetched or stored (no cookie) before the 18+ tap.
  // One tap, then home: the tour is an invitation (NewHere), not a wall before the first line.
  else if (!age) screen = <Gate onOk={() => { setAgeOk(); setAge(true); }} meta={meta} />;
  else if (route.kind === "plates") screen = <Album goHome={() => nav("/")} open={(sid) => nav(`/s/${sid}`)} />;
  else screen = <Page key={id ?? "home"} id={id} skin={skin} setSkin={setSkin} nav={nav} meta={meta} cloth={cloth} closeCloth={() => setCloth(false)} />;
  return (
    <>
      {screen}
      {help === "tour" && age && <Onboarding skin={skin} inStory={route.kind === "story"} onDone={() => setHelp(null)} onList={() => setHelp("list")} />}
      {help === "list" && <ShortList at={helpAt} onTour={() => setHelp("tour")} onClose={() => { setHelp(null); setHelpAt(undefined); }} />}
      {sheet === "feedback" && <FeedbackSheet page={page} send={api.feedback} onClose={() => setSheet(null)} />}
      {sheet === "stories" && <StoriesSheet current={/^\/s\/([^/]+)/.exec(location.pathname)?.[1] ?? null} nav={nav} onClose={() => setSheet(null)} />}
      <Toast />
      <DeleteStorySheet nav={nav} />
      {sheet === "patron" && (
        <Sheet label="Patron" onClose={() => setSheet(null)}>
          <PatronPanel reason={patronWhy} />
          <div className="lm-sheet-actions"><button type="button" onClick={() => setSheet(null)}>Close</button></div>
        </Sheet>
      )}
      {sheet === "delete" && (
        <DeleteSheet forget={api.forget} onClose={() => setSheet(null)} onDone={() => { setSheet(null); setDeleted(true); history.replaceState(null, "", "/"); }} />
      )}
    </>
  );
}

/** "Delete this story?" from inside a story: the app's own sheet, then home with "Story deleted." */
function DeleteStorySheet({ nav }: { nav(path: string, replace?: boolean): void }) {
  const [story, setStory] = useState<{ id: string; title: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const on = (e: Event) => setStory((e as CustomEvent<{ id: string; title: string }>).detail);
    window.addEventListener("weftwake:delete-story", on);
    return () => window.removeEventListener("weftwake:delete-story", on);
  }, []);
  if (!story) return null;
  const close = () => { if (!busy) setStory(null); };
  const go = async () => {
    setBusy(true);
    try { await api.remove(story.id); setStory(null); nav("/"); toast("Story deleted."); }
    catch { toast("Couldn't delete it. Check your connection and try again."); }
    finally { setBusy(false); }
  };
  return (
    <Sheet label="Delete this story" onClose={close}>
      <h2 className="lm-sheet-h">Delete “{story.title}”?</h2>
      <p>The story, its plates and its look-closer pages are deleted for good. This can't be undone.</p>
      <div className="lm-sheet-actions">
        <button type="button" onClick={close} disabled={busy}>Keep it</button>
        <button type="button" className="lm-danger" onClick={go} disabled={busy}>{busy ? "Deleting…" : "Delete for good"}</button>
      </div>
    </Sheet>
  );
}

/** A message that stays a few seconds on the screen the player lands on (useStory toast()). */
/** A note's text (the box that scrolls): its last line fades while there is more below, so a cut-off recap says so. */
function NoteBody({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mark = () => el.classList.toggle("lm-more", el.scrollHeight - el.clientHeight - el.scrollTop > 4);
    mark();
    el.addEventListener("scroll", mark, { passive: true });
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(mark) : null;
    ro?.observe(el);
    for (const c of el.children) ro?.observe(c);
    return () => { el.removeEventListener("scroll", mark); ro?.disconnect(); };
  }, []);
  return <div ref={ref}>{children}</div>;
}

function Toast() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    let t: number | undefined;
    const on = (e: Event) => { setMsg((e as CustomEvent<string>).detail); window.clearTimeout(t); t = window.setTimeout(() => setMsg(null), 6000); };
    window.addEventListener("weftwake:toast", on);
    return () => { window.removeEventListener("weftwake:toast", on); window.clearTimeout(t); };
  }, []);
  if (!msg) return null;
  return <div className="lm-toast" role="status"><span>{msg}</span><button type="button" aria-label="Dismiss" onClick={() => setMsg(null)}>×</button></div>;
}

/** For a first visit: a small invitation to the tour, until it's taken or dismissed. */
function NewHere({ help }: { help(): void }) {
  const [shown, setShown] = useState(() => !onboarded());
  if (!shown) return null;
  return (
    <div className="lm-newhere" role="note">
      <button type="button" className="lm-newhere-go" onClick={() => { setShown(false); help(); }}>New here? See how to play</button>
      <button type="button" className="lm-newhere-x" aria-label="Dismiss" onClick={() => { markOnboarded(); setShown(false); }}>×</button>
    </div>
  );
}

/** The music on the home screen: one calm ensemble and feel per visit. */
const HOME_MUSIC: MusicTags = (() => {
  const pick = <T,>(xs: T[]) => xs[Math.floor(Math.random() * xs.length)]!;
  return { ensemble: pick<Ensemble>(["felt piano", "harp", "solo cello", "acoustic guitar", "woodwinds"]), feel: pick<Feel>(["serene", "tender", "wonder"]) };
})();

/** Coming back to a story after this long shows what came before. */
const PREVIOUSLY_AFTER_MS = 3 * 60 * 60 * 1000;

function Page({ id, skin, setSkin, nav, meta, cloth, closeCloth }: {
  id: string | null; skin: Skin; setSkin(s: Skin): void; nav(path: string, replace?: boolean): void; meta(m: Meta): void;
  cloth: boolean; closeCloth(): void;
}) {
  const [stories, setStories] = useState<StorySummary[]>([]);
  const [started, setStarted] = useState(false);
  useEffect(() => { if (!id) api.stories().then((r) => setStories(r.stories)).catch(() => {}); }, [id]);
  const s = useStory(id, { nav, onStart: () => setStarted(true), onMeta: meta });
  // The account page's "Back to your story" returns here (a story begun on this page has no id prop: s.id).
  useEffect(() => { if (s.id) rememberStory(s.id); }, [s.id]);
  useInkReveal();
  // The scene's soundscape (heard only if the player turned sound on); the home screen is quiet. s.id, not id: a story
  // started on this page has no id prop (its address is set with replaceState), and it should be heard too.
  useEffect(() => { sound.play(s.id && s.view ? s.view.ambience : "silence"); }, [s.id, s.view?.ambience]);
  // The score follows the story's tension: the reply being written, else the latest turn; a crisis tightens it.
  const lastTurn = s.view?.turns.at(-1);
  const tension = s.live?.tension ?? lastTurn?.tension ?? s.view?.tension ?? 2;
  const crisis = !!(s.view?.chapter?.crisis || s.view?.crisis);
  // Away from a story (home, sign-in, privacy) the score plays on, calm: a gentle ensemble chosen for this visit.
  useEffect(() => { sound.setMood(s.id && s.view ? { tension, crisis } : { tension: 1.6, crisis: false }); }, [s.id, !!s.view, tension, crisis]);
  const music = s.id ? s.view?.music ?? null : HOME_MUSIC;
  useEffect(() => { sound.setTags(music); }, [music?.ensemble, music?.feel]);
  // A sudden event rings once, when its turn arrives (not when an old one is loaded).
  const seenTurn = useRef<number | null>(null);
  useEffect(() => {
    if (!lastTurn) return;
    if (seenTurn.current !== null && lastTurn.n > seenTurn.current && lastTurn.spike) sound.accent();
    seenTurn.current = lastTurn.n;
  }, [lastTurn?.n]);
  // Three suggested openings, drawn at random once per visit (a story's start shouldn't be everyone's).
  const [suggestions] = useState(() => shuffleOpenings(3));
  const home: HomeBits | null = !id && !started ? {
    stories, seeds: suggestions, open: (sid) => nav(`/s/${sid}`),
    remove: async (sid) => { await api.remove(sid); setStories((cur) => cur.filter((x) => x.id !== sid)); },
  } : null;
  const Theme = THEMES[skin];
  // Opening a story: the morning first if the loom dreamt it since the last visit, then the latest turn (not row 1).
  const [morning, setMorning] = useState(false);
  const opened = useRef(false);
  const openedDream = useRef<number | null>(null);
  const [previous, setPrevious] = useState<string | null>(null);
  const [newSince, setNewSince] = useState<Change[]>([]);
  // V2: what happened while the player was away, when the morning didn't show it (seen there, or no morning on this device).
  const [meanwhile, setMeanwhile] = useState<string[] | null>(null);
  const [hereSheet, setHereSheet] = useState(false);
  const [bigMap, setBigMap] = useState(false);
  // Pass it on: the sheet for this story's opening, and an opening someone passed on (/play?seed=…) on the home screen.
  const [passOn, setPassOn] = useState(false);
  useEffect(() => {
    const on = () => setPassOn(true);
    window.addEventListener("weftwake:passon", on);
    return () => window.removeEventListener("weftwake:passon", on);
  }, []);
  const [seedCode, setSeedCode] = useState(() => (!id ? new URLSearchParams(location.search).get("seed") : null));
  const closeSeed = () => { setSeedCode(null); history.replaceState(null, "", location.pathname); };
  useEffect(() => {
    const on = () => setBigMap(true);
    window.addEventListener("weftwake:bigmap", on);
    return () => window.removeEventListener("weftwake:bigmap", on);
  }, []);
  // Leaving the full-screen map for the story (a look, a line to send): everything over the story closes.
  const backToStory = () => { setBigMap(false); setHereSheet(false); setMap(null); };
  useEffect(() => {
    const on = () => setHereSheet(true);
    window.addEventListener("weftwake:here", on);
    return () => window.removeEventListener("weftwake:here", on);
  }, []);
  // It goes when the player writes on.
  useEffect(() => { if (s.live || s.pending) { setPrevious(null); setNewSince([]); setMeanwhile(null); } }, [!!s.live, !!s.pending]);
  const toLatest = () => { const n = s.view?.turns.at(-1)?.n; if (n) requestAnimationFrame(() => scrollToLatest(n)); };
  useEffect(() => {
    if (!s.view || opened.current) return;
    opened.current = true;
    openedDream.current = s.view.dreamt?.at ?? null;
    // Endings after this turn happen now (their ceremony plays); ones already there show their last frame.
    noteOpened(s.view.id, s.view.turns.at(-1)?.n ?? 0);
    sound.storyOpened();
    // Back after a while: a line of what came before, where you pick up.
    if (id && s.view.recap.trim() && s.view.lastAt && Date.now() - s.view.lastAt > PREVIOUSLY_AFTER_MS && !s.view.ended) setPrevious(s.view.recap.trim());
    // Back since something changed because of what players told us: that, once.
    if (id) setNewSince(takeNewSince(s.view.lastAt));
    // The morning shows what happened while the player was away; without one, the note at the top does, above "Previously…".
    const away = !morningDue(s.view) ? meanwhileDue(s.view) : null;
    if (id && away) { setMeanwhile(away); markMeanwhileSeen(s.view); }
    if (morningDue(s.view)) setMorning(true);
    else if (id) toLatest(); // an existing story opens at the latest turn; a new one is already there
  }, [id, s.view]);
  // The journal (find, threads, places, kept, chapters): from the rail, a footer link, "Chapter N" above the line (its
  // chapters), or "journal" typed into the Line.
  const [map, setMap] = useState<"find" | "chapters" | "journal" | "places" | null>(null);
  useEffect(() => {
    const on = (e: Event) => { const d = (e as CustomEvent<"chapters" | "places" | undefined>).detail; setMap(d === "chapters" || d === "places" ? d : "journal"); };
    window.addEventListener("weftwake:map", on);
    return () => window.removeEventListener("weftwake:map", on);
  }, []);
  // A dream that lands while the story is open (the first dream, mid-session; "dream now", beta) wakes the player up the
  // same way, as one dreamt while they played.
  const [dreamtInPlay, setDreamtInPlay] = useState(false);
  // Only a dream that landed after the story opened (this effect also runs as the story opens, with the night's dream).
  useEffect(() => {
    if (s.view && opened.current && (s.view.dreamt?.at ?? null) !== openedDream.current && morningDue(s.view)) { setDreamtInPlay(true); setMorning(true); }
  }, [s.view?.dreamt?.at]);
  // V2: the page takes the scene's weather, light and crowd; a new place tears the band.
  const world = useWorld(s);
  return (
    <>
      <SkyLayer sky={morning ? null : world.sky} />
      <Tear moved={world.moved} />
      <Theme s={s} home={home} skin={skin} setSkin={setSkin} goHome={() => nav("/")} openPlates={() => nav("/plates")} meta={meta} />
      {morning && s.view && <Morning view={s.view} inPlay={dreamtInPlay} onDone={() => { setMorning(false); setDreamtInPlay(false); toLatest(); }} />}
      {cloth && s.view && <Cloth view={s.view} onClose={closeCloth} />}
      {s.id && s.view && !morning && <StoryRail view={s.view} onOpen={setMap} />}
      {s.id && s.view && !morning && <EndsNav view={s.view} />}
      {s.id && s.view && !morning && <BackChip />}
      <StoryStatus s={s} />
      {passOn && s.id && <PassOn id={s.id} onClose={() => setPassOn(false)} />}
      {seedCode && !s.id && !started && (
        <SeedWelcome code={seedCode} onClose={closeSeed} onBegin={(seed) => { closeSeed(); s.beginSeed(seed); }} />
      )}
      {bigMap && s.view && (
        <BigMap view={s.view} onClose={() => setBigMap(false)}
          onLook={(name) => { backToStory(); const n = s.view?.turns.at(-1)?.n; if (n) { s.look(n, name); requestAnimationFrame(() => scrollToLatest(n)); } }}
          onFill={(text) => { backToStory(); s.fill(text); }} onPull={(t) => { backToStory(); s.pull(t); }} />
      )}
      {hereSheet && s.view && (
        <Sheet label="Where you are" onClose={() => setHereSheet(false)}>
          <div className="lm-here-sheet">
            <WhereYouAre view={s.view} onLook={(name) => { setHereSheet(false); const n = s.view?.turns.at(-1)?.n; if (n) { s.look(n, name); requestAnimationFrame(() => scrollToLatest(n)); } }} />
            <button type="button" onClick={() => setHereSheet(false)}>Close</button>
          </div>
        </Sheet>
      )}
      {(previous || meanwhile) && !morning && (
        // One note: what happened while the player was away first, then where they left off.
        <aside className={`lm-previously${meanwhile ? " lm-meanwhile-note" : ""}`} role="note">
          <NoteBody>
            {meanwhile && <Meanwhile lines={meanwhile} />}
            {previous && <p><span className="lm-previously-h">Previously</span> {previous}</p>}
          </NoteBody>
          <button type="button" aria-label="Close" onClick={() => { setPrevious(null); setMeanwhile(null); }}>×</button>
        </aside>
      )}
      {newSince.length > 0 && !previous && !meanwhile && !morning && (
        <aside className="lm-previously lm-new-since" role="note">
          <NoteBody>
            <p className="lm-previously-h">New since your last visit</p>
            <ul>{newSince.map((c) => <li key={c.date + c.text}>{c.text}</li>)}</ul>
            <p className="lm-new-since-why">Changed because of what players told us.</p>
          </NoteBody>
          <button type="button" aria-label="Close" onClick={() => setNewSince([])}>×</button>
        </aside>
      )}
      {s.id && s.view && <h1 className="lm-sr">{s.view.title}</h1>}
      {!s.id && !started && <NewHere help={() => meta("tour")} />}
      {map && s.view && <StoryMap view={s.view} mode={map} onClose={() => setMap(null)} onChanged={s.reload} onPull={s.pull} onFill={s.fill}
        onLook={(name) => { const n = s.view?.turns.at(-1)?.n; if (n) { s.look(n, name); requestAnimationFrame(() => scrollToLatest(n)); } }} />}
    </>
  );
}
